package gr.tech.site.pages.landings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Background
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.css.functions.toImage
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initHvacRebatesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Commercial HVAC Rebates — Energy Incentives for Cultivation Facilities",
        description = "HVAC systems in cultivation facilities qualify for utility rebate programs and energy savings incentives. Green Rebates identifies every available HVAC rebate and manages every application.",
        canonicalUrl = "https://greenrebates.com/hvac-rebates",
        ogImage = "/images/ledgrowlights2.jpg"
    ))
}

@Page(SitePage.Landing.HVAC_REBATES)
@Layout(".components.layouts.PageLayout")
@Composable
fun HvacRebatesPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        // TODO: Replace ImageRepo.LEDGrowLights2 with a dedicated HVAC/mechanical room image
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        ) {
            Img(
                src = ImageRepo.LEDGrowLights2.src,
                alt = ImageRepo.LEDGrowLights2.alt,
                attrs = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .toAttrs { attr("fetchpriority", "high") }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .background(
                        Background.of(
                            image = linearGradient(
                                from = Theme.DarkGreen.rgb,
                                to = rgba(2, 67, 66, 0),
                                dir = LinearGradient.Direction.ToRight
                            ).toImage()
                        )
                    )
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(attrs = LandingHeroEyebrowStyle.toModifier().classNames("text-uppercase", "mb-2").toAttrs()) {
                    Text("Commercial HVAC Rebates for Cultivation Facilities")
                }
                H1(attrs = LandingHeroHeadlineStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Cut HVAC Costs With Energy Incentive Programs")
                }
                P(attrs = LandingHeroLeadStyle.toModifier().classNames("mb-4").toAttrs()) {
                    Text("HVAC systems are one of the largest operational expenses in any cultivation facility. Green Rebates identifies HVAC energy incentives and savings programs available through your utility — and manages every application on your behalf.")
                }
                Link(
                    path = "/contact-us",
                    text = "Get Your Free HVAC Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaPrimaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free commercial HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // WHO QUALIFIES — leads with audience before types
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-4").toAttrs()) {

                    // Left — copy + checklist + CTA
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Who Qualifies for Commercial HVAC Rebates")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("Whether you're upgrading a full climate control system or replacing aging HVAC equipment, Green Rebates finds every commercial HVAC rebate available for your cultivation facility.")
                            }
                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "Indoor cultivation and cannabis facilities upgrading commercial HVAC systems",
                                    "Greenhouse operations replacing or retrofitting HVAC equipment for energy savings",
                                    "Commercial growers reducing energy costs through HVAC efficiency improvements"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }
                            Link(
                                path = "/contact-us",
                                text = "Find Out If Your Facility Qualifies \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Find out if your cultivation facility qualifies for HVAC rebates") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }

                    // Right — image
                    // TODO: Replace with a dedicated HVAC/mechanical room image
                    Div(attrs = Modifier.classNames("col-lg-6").toAttrs()) {
                        Img(
                            src = ImageRepo.CannabisPhoto.src,
                            alt = ImageRepo.CannabisPhoto.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }
                }
            }
        }

        // TYPES OF HVAC REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("Types of HVAC Rebates Available")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Utility-backed HVAC energy incentives and savings programs for indoor cultivation facilities and commercial grow operations.")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4").toAttrs()) {
                    listOf(
                        "Commercial HVAC Rebates" to
                            "Utility programs offer direct rebates for commercial HVAC equipment upgrades in qualifying cultivation facilities — often covering a substantial share of replacement costs.",
                        "HVAC Energy Incentives" to
                            "Energy incentive programs reward growers who reduce facility energy consumption through high-efficiency HVAC upgrades, including new installs and system replacements.",
                        "HVAC Energy Savings Programs" to
                            "State and utility-backed energy savings programs provide ongoing incentives for commercial operations that commit to HVAC efficiency improvements.",
                        "Grow Operation Equipment Rebates" to
                            "Cultivation-specific incentive programs often bundle HVAC rebates with LED and dehumidifier upgrades — maximizing total payout for your facility."
                    ).forEachIndexed { index, (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingRebateCardStyle.toModifier().toAttrs()) {
                                when (index) {
                                    0 -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                    Text(desc)
                                }
                            }
                        }
                    }
                }
            }
        }

        // WHY ACT NOW
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-7")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingDarkSectionHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Why Cultivation Facilities Can't Afford to Delay HVAC Rebates")
                }
                P(attrs = LandingDarkBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("HVAC is one of the biggest line items on any indoor grow operation's energy bill — and utility rebate programs for commercial HVAC are oversubscribed. Funding windows close without notice. Green Rebates locks in your pre-approval before program funding runs out.")
                }
                Link(
                    path = "/contact-us",
                    text = "Check Your HVAC Rebate Eligibility Now \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaOutlineStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Check your commercial HVAC rebate eligibility now") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("How Green Rebates Secures Your HVAC Incentives")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Three steps to maximize HVAC energy rebates for your indoor grow or cultivation facility.")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4").toAttrs()) {
                    listOf(
                        Triple("1", "Contact Us — We Assess Your HVAC Needs",
                            "Share details about your facility's HVAC systems and utility provider. We identify every applicable rebate and incentive program available to your operation."),
                        Triple("2", "We Manage the Entire Application",
                            "Our team handles all documentation, submissions, and utility coordination for your HVAC rebate — so you stay focused on running your grow operation."),
                        Triple("3", "You Collect the Savings",
                            "Once approved, your HVAC rebate check arrives with no follow-up required from your team. We handle it all so you don't have to.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3").toAttrs()) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(attrs = LandingStepCardTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                P(attrs = LandingStepCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                            }
                        }
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingDarkSectionHeadingStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Why Growers Choose Green Rebates for HVAC Incentives")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4").toAttrs()) {
                    listOf(
                        "HVAC Rebate Specialists" to
                            "We know commercial HVAC rebate programs inside out — from rooftop units to chilled water systems used in large-scale grow operations. We know what qualifies and how to get it approved.",
                        "Pre-Approval Fast Track" to
                            "Our utility relationships allow us to get cultivation facilities pre-approved faster than standard application channels — protecting your spot in the program.",
                        "Full Application Ownership" to
                            "We own the entire process from facility audit through final rebate payment. HVAC, LED, dehumidifiers, or all three — we handle everything.",
                        "Maximize Total Incentive Funding" to
                            "We look for every available HVAC energy savings program and stacking opportunity — combining HVAC rebates with LED and dehumidifier incentives to get your facility the maximum possible payout."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                                }
                            }
                        }
                    }
                }
            }
        }

        // TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("What Growers Are Saying About HVAC Rebates")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates handled our entire HVAC rebate application from start to finish. We recovered far more than we expected on our cooling system upgrade — and never had to deal with the utility directly." to
                            "Cultivation Facility Owner — Oregon",
                        "I was skeptical that HVAC rebates would amount to much. Green Rebates proved me wrong — and did it without any extra work on our end." to
                            "Commercial Grower — New Jersey"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1")
                                        .toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("— $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        RelatedSection(listOf(
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/rebates-incentives-for-indoor-growers-what-you-need-to-know" to "Rebates vs. Incentives for Indoor Growers: What You Need to Know",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingCtaHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Ready to Reduce Your HVAC Costs?")
                }
                P(attrs = LandingCtaBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("Commercial HVAC rebates, HVAC energy incentives, and energy savings programs are available for qualifying cultivation facilities nationwide. Green Rebates finds them all and handles every application — no account required.")
                }
                Link(
                    path = "/contact-us",
                    text = "Contact Us for a Free HVAC Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free commercial HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
